import React from 'react';

import {addOrReplaceAndRunAnalysisStep} from 'analysis-tool/AnalysisActions';
import {SaveToResultsStepDataName} from 'analysis-tool/AnalysisStepDataNames';
import {buildPreviewOverRowLimitMessage} from 'analysis-tool/data-frame-creation/DataFrameCreationMessages';
import useDataMappingState from 'analysis-tool/structured-data-mapping/useDataMappingState';
import validateSchemaMapping from 'analysis-tool/structured-data-mapping/validateDataMapping';
import getDefaultMainViewItemForAnalysis from 'analysis-tool/ui/getDefaultMainViewItemForAnalysis';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import StepConfigurationViewTemplate from 'analysis-tool/ui/view-templates/StepConfigurationViewTemplate';
import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {ActionMethod, ActionOrigin, ANALYSIS_TOOL_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {
  GetSubsetDataFrame,
  GetSubsetDataFrameVariables,
} from 'data-frames/graphql/__generated/GetSubsetDataFrame';
import {DEFAULT_DATA_FRAME_ROW_LIMIT, GET_SUBSET_DATA_FRAME_QUERY} from 'data-frames/graphql/GetDataFrame';
import getUserColumns from 'data-frames/util/getUserColumns';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {SAVE_TO_RESULTS_STARTED_EVENT} from 'performance/processAnalysesEvents';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {updateMappings, updateResultSchema} from './DataMappingStateReducers';
import {DataMappingState} from './DataMappingTypes';
import SaveToResultsStepOutputPreview from './SaveToResultsStepOutputPreview';
import SaveToResultsStepper from './SaveToResultsStepper';

const isFinalizingGlobalState = makeUseGlobalClientState<boolean>(false);

function trackAddSaveToResultsStepEvent(
  dataMappingState: DataMappingState,
  dataFrame: DataFrame,
  dataFrameId: string
): void {
  const numDataFrameColumns = getUserColumns(dataFrame).length;
  const numResultSchemaFields = dataMappingState.resultSchema!.fields.length;
  const numMappings = dataMappingState.mappings.length;

  trackEvent(ANALYSIS_TOOL_ANALYTICS_ACTIONS.RESULTS_COLUMNS_MAPPED, {
    action_method: ActionMethod.BUTTON_CLICK,
    action_origin: ActionOrigin.ANALYSIS_TOOL_SAVE_TO_RESULTS_CONFIGURATION_VIEW,
    data_frame_id: dataFrameId,
    result_schema_id: dataMappingState.resultSchema!.id,
    num_result_columns: numResultSchemaFields,
    num_data_frame_columns: numDataFrameColumns,
    num_mappings: numMappings,
  });
}

export interface InternalSaveToResultsStepConfigurationViewProps {
  clientStateId: string;
  inputDataFrame: DataFrame;
  dataMappingState: DataMappingState;
  setDataMappingState: (newDataMappingState: DataMappingState) => void;
  addSaveToResultsStep: () => Promise<void>;
  onCancel: () => void;
}

export function InternalSaveToResultsStepConfigurationView({
  clientStateId,
  inputDataFrame,
  dataMappingState,
  setDataMappingState,
  addSaveToResultsStep,
  onCancel,
}: InternalSaveToResultsStepConfigurationViewProps): JSX.Element {
  const [isFinalizing, setIsFinalizing] = isFinalizingGlobalState.useGlobalClientState(clientStateId);

  const onFinalize = async (): Promise<void> => {
    setIsFinalizing(true);
    await addSaveToResultsStep();
    setIsFinalizing(false);
  };

  const validationErrors = validateSchemaMapping(dataMappingState);

  const errorMessage =
    inputDataFrame.numRows && inputDataFrame.rows.length < inputDataFrame.numRows
      ? buildPreviewOverRowLimitMessage(DEFAULT_DATA_FRAME_ROW_LIMIT)
      : null;

  return (
    <StepConfigurationViewTemplate
      clientStateId={clientStateId}
      dataFrames={[inputDataFrame]}
      renderAdditionalPreviewContent={() => (
        <SaveToResultsStepOutputPreview
          clientStateId={`${clientStateId}_preview`}
          inputDataFrame={inputDataFrame}
          dataMappingState={dataMappingState}
          targetSpecificMappingState={null}
        />
      )}
      disableFinalizeButton={
        dataMappingState.resultSchema == null ||
        dataMappingState.mappings.length === 0 ||
        validationErrors.length > 0
      }
      finalizeButtonText="Save to Results"
      finalizingLoadingMessage="Creating Results..."
      messages={errorMessage != null ? [errorMessage] : []}
      onCancel={onCancel}
      onFinalize={onFinalize}
      title="Save to Results"
      isFinalizing={isFinalizing}
    >
      <SaveToResultsStepper
        resultSchema={dataMappingState.resultSchema}
        inputDataFrameSpec={inputDataFrame}
        columnMapping={dataMappingState.mappings}
        onSchemaChange={(newResultSchema) => {
          setDataMappingState(updateResultSchema(dataMappingState, newResultSchema, inputDataFrame.columns));
        }}
        onMappingsChange={(newMappings) => {
          setDataMappingState(updateMappings(dataMappingState, newMappings));
        }}
      />
    </StepConfigurationViewTemplate>
  );
}

export type SaveToResultsStepConfigurationViewProps = Pick<
  MainViewComponentProps<AnalysisMainViewItemType.SAVE_TO_RESULTS_CONFIGURATION>,
  'analysis' | 'clientStateId' | 'onCloseStepInitializationView' | 'selectedItem'
>;

export default function SaveToResultsStepConfigurationView(
  props: SaveToResultsStepConfigurationViewProps
): JSX.Element {
  // `dataMappingState` may be partially initialized in `<DataMappingModal>`.
  const [dataMappingState, setDataMappingState, resetDataMappingState] = useDataMappingState(
    props.clientStateId,
    props.selectedItem.inputDataFrameId
  );

  const addSaveToResultsStep = async (
    dataMappingState: DataMappingState,
    dataFrame: DataFrame
  ): Promise<void> => {
    trackAddSaveToResultsStepEvent(dataMappingState, dataFrame, props.selectedItem.inputDataFrameId);
    logProfilerTimingEvent(SAVE_TO_RESULTS_STARTED_EVENT);
    const {id: stepId} = await addOrReplaceAndRunAnalysisStep(props.analysis.id, {
      stepType: PipelineStepType.SAVE_TO_RESULTS,
      stepVersion: 2,
      stepConfig: {
        schemaId: dataMappingState.resultSchema!.id,
        columnMapping: dataMappingState.mappings,
        targetSpecificMapping: null,
      },
      stepInputParents: {
        [SaveToResultsStepDataName.INPUT_DATASET]: [props.selectedItem.inputDataReference],
      },
      stepInput: {
        inputDataFrameId: props.selectedItem.inputDataFrameId,
      },
    });
    props.onCloseStepInitializationView({type: AnalysisMainViewItemType.SAVE_TO_RESULTS_OUTPUT, stepId});
  };

  const onCancel = (): void => {
    props.onCloseStepInitializationView(getDefaultMainViewItemForAnalysis(props.analysis));
    resetDataMappingState();
  };

  return (
    <BenchlingQuery<GetSubsetDataFrame, GetSubsetDataFrameVariables>
      query={GET_SUBSET_DATA_FRAME_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{dataFrameId: props.selectedItem.inputDataFrameId, limit: DEFAULT_DATA_FRAME_ROW_LIMIT}}
    >
      {({dataFrame}) => {
        assert(!isInaccessible(dataFrame), 'Expected data frame to be accessible.');
        return (
          <InternalSaveToResultsStepConfigurationView
            clientStateId={props.clientStateId}
            inputDataFrame={dataFrame.dataFrameJson}
            dataMappingState={dataMappingState}
            setDataMappingState={setDataMappingState}
            addSaveToResultsStep={() => addSaveToResultsStep(dataMappingState, dataFrame.dataFrameJson)}
            onCancel={onCancel}
          />
        );
      }}
    </BenchlingQuery>
  );
}
